import React, { memo, Suspense, useCallback, useMemo, useState } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import { ModalCancel } from '../../../../../../views/modal-cancel'
import { useSelector } from 'react-redux'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { getCardCurrentStage, getCardStages, getVisibleFormData } from '../../../selectors'
import { getClaimWorkData } from '../../../../../../store/claim_work/action-creators'
import { ModalCreateTask } from 'src/react-app/views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { ClaimWorkEditDateModal } from '../claim-work-edit-date-modal'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../../ui/Steps'
import { EditOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons'

type TStage = 'schedule' | 'work_completed' | 'completed' | 'cancelled'

interface FormsHeaderProps {
  id: string | number
  nextStageOnClick?: (stage: TStage) => Promise<void>
}

export const FormsHeader: React.FC<FormsHeaderProps> = memo((props: FormsHeaderProps) => {
  const { id, nextStageOnClick } = props
  const [isOpenModalCancel, setIsOpenModalCancel] = useState<boolean>(false)
  const [isOpenModalEditDate, setIsOpenModalEditDate] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const visibleFormData = useSelector(getVisibleFormData)
  const stageObj = useSelector(getCardStages)
  const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
  const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
  const currentStage = useSelector(getCardCurrentStage)

  const handleCancelOnClick = useCallback(() => {
    if (visibleFormData) {
      const isRequiredFields = isFormDataRequired(
        visibleFormData?.resultObject,
        visibleFormData?.resultSchema,
      )
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
    }

    setIsOpenModalCancel(true)
  }, [visibleFormData])

  const onCloseCancel = useCallback(() => {
    setIsOpenModalCancel(false)
  }, [setIsOpenModalCancel])

  const onCloseAddNote = useCallback(() => {
    setIsOpenModalAddNote(false)
  }, [setIsOpenModalAddNote])

  const onCancel = useCallback(async () => {
    await dispatch(getClaimWorkData(id))
    dispatch(getTimeLineData('claim_work', id))
    setIsOpenModalCancel(false)
  }, [id, dispatch])

  const buttonsDisabled = useMemo(() => {
    return !(stageObj?.items !== undefined && stageObj?.current !== undefined)
  }, [stageObj])

  const disabledButtons = useMemo(() => {
    if (!stageObj?.items || typeof stageObj?.current !== 'number') {
      return true
    }
    const items = Object.values(stageObj?.items) as any[]
    const currentStageCode = items?.[stageObj?.current]?.code
    return !(currentStageCode && !['completed', 'cancelled'].includes(currentStageCode))
  }, [stageObj])

  const onCloseCreateTask = useCallback(() => {
    setIsOpenModalCreateTask(false)
  }, [])

  const onCloseEditDateModal = useCallback(() => {
    setIsOpenModalEditDate(false)
  }, [])

  const handleToSchedule = useCallback(async () => {
    await nextStageOnClick?.('schedule')
  }, [nextStageOnClick])

  const handleToWorkCompleted = useCallback(async () => {
    await nextStageOnClick?.('work_completed')
  }, [nextStageOnClick])

  const handleToCompleted = useCallback(async () => {
    await nextStageOnClick?.('completed')
  }, [nextStageOnClick])

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'to-schedule',
        label: 'В график',
        type: 'primary',
        onClick: handleToSchedule,
        isPrimary: true,
        disabled: buttonsDisabled,
        condition: currentStage === 0,
        order: 1,
      },
      {
        key: 'to-work-completed',
        label: 'Работы произведены',
        type: 'primary',
        onClick: handleToWorkCompleted,
        isPrimary: true,
        disabled: buttonsDisabled,
        condition: currentStage === 1,
        order: 1,
      },
      {
        key: 'to-completed',
        label: 'Выполнена',
        type: 'primary',
        onClick: handleToCompleted,
        isPrimary: true,
        disabled: buttonsDisabled,
        condition: currentStage === 2,
        order: 1,
      },
      {
        key: 'add-note',
        label: 'Добавить заметку',
        icon: <EditOutlined />,
        onClick: () => setIsOpenModalAddNote(true),
        disabled: disabledButtons,
        condition: true,
        order: 10,
      },
      {
        key: 'edit-date',
        label: 'Изменить плановую дату завершения',
        icon: <CalendarOutlined />,
        onClick: () => setIsOpenModalEditDate(true),
        disabled: disabledButtons,
        condition: true,
        order: 15,
      },
      {
        key: 'cancel',
        label: 'Отменить',
        icon: <CloseOutlined />,
        danger: true,
        onClick: handleCancelOnClick,
        disabled: disabledButtons,
        condition: false,
        order: 100,
      },
    ],
    [
      currentStage,
      buttonsDisabled,
      disabledButtons,
      handleToSchedule,
      handleToWorkCompleted,
      handleToCompleted,
      handleCancelOnClick,
    ],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <>
      <HStack gap={'8'} className={'mapWrapper'} wrap>
        <div>
          {stageObj?.items && typeof stageObj?.current === 'number' && stageObj?.history && (
            <HSteps
              items={Object.values(stageObj?.items)}
              current={stageObj?.current || 0}
              history={stageObj?.history}
            />
          )}
        </div>
        <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
      </HStack>

      <Suspense fallback={''}>
        <ModalCreateTask
          isOpen={isOpenModalCreateTask}
          onClose={onCloseCreateTask}
          entityType={'claim_work'}
          entityId={id}
        />
      </Suspense>

      <ModalAddNote
        entityId={Number(id)}
        entityType={'claim_work'}
        isOpen={isOpenModalAddNote}
        onClose={onCloseAddNote}
      />

      <ClaimWorkEditDateModal
        id={id}
        isModalOpen={isOpenModalEditDate}
        onCancel={onCloseEditDateModal}
      />

      <Suspense fallback={''}>
        <ModalCancel
          entityId={id}
          entityType="claim_work"
          isOpen={isOpenModalCancel}
          onClose={onCloseCancel}
          onCancel={onCancel}
          title={'Отмена работы по рекламации'}
        />
      </Suspense>
    </>
  )
})

FormsHeader.displayName = 'FormsHeader'
